import {useTranslation} from "react-i18next";
import DefaultDialog, {DefaultDialogSize} from "../../components/Dialog/ClosableDialog";
import {Button, FormControl, InputLabel, MenuItem, Select, Stack} from "@mui/material";
import {HardwareType, useSessionQuery} from "../../app/api/api";
import {useFormik} from "formik";
import * as yup from "yup";
import {useSnackbar} from "notistack";
import {FilterOperator} from "../../components/ExTable";
import {getIsSuccessfulResponse} from "../../utils/getIsSuccessfulResponse";
import {ResponseErrorAlert, ResponseMessage} from "../../components/ResponseErrorMessages";
import {FirmwareField, FirmwareState} from "../../app/api/model/firmware";
import {FirmwareAutocomplete} from "../../components/input/AsyncAutocomplete/variants";
import {PermissionLevel} from "../../app/api/model/user";
import {configuration} from "../../config/config";

interface DeviceCsvImportDialogProps {
    open: boolean;
    onClose?: () => void;
}

export default function DeviceImportCsvDialog({open, onClose}: DeviceCsvImportDialogProps) {
    const {t} = useTranslation();
    const {data: session} = useSessionQuery();
    const {enqueueSnackbar} = useSnackbar();

    const validationSchema = yup.object({
        hardwareType: yup.string().required(t("validation_inputRequired")),
        firmwareId: yup.number().required(t("validation_inputRequired")),
        file: yup.mixed(),
    });

    const formik = useFormik({
        initialValues: {
            file: undefined,
            hardwareType: "",
            firmwareId: 1,
        },
        validationSchema: validationSchema,
        onSubmit: async (values, submitProps) => {
            const formData = new FormData();

            if (values.firmwareId && values.file) {
                formData.append("hardwareType", values.hardwareType);
                formData.append("firmwareId", `${values.firmwareId}`);
                formData.append("file", values.file);
            }

            const res = await fetch(`${configuration.apiUrl}/devices/csv-import`, {
                method: "POST",
                credentials: "include",
                body: formData,
            });

            if (getIsSuccessfulResponse(res.status)) {
                enqueueSnackbar(t("generic_savedSuccessfully"), {variant: "success"});
                submitProps.resetForm({values});
            } else {
                const data = await res.json();
                data?.messages.map((m: ResponseMessage) =>
                    enqueueSnackbar(<ResponseErrorAlert title={t(m.title)} body={t(m.body)} />, {variant: "error"})
                );
                submitProps.resetForm({values});
            }
        },
    });

    const handleClose = () => {
        onClose?.();
        formik.resetForm();
    };

    const handleSubmit = () => {
        formik.handleSubmit();
        handleClose();
    };

    return (
        <DefaultDialog
            open={open}
            onClose={handleClose}
            size={DefaultDialogSize.MEDIUM}
            titleKey={"action_createDeviceViaCsv"}
            actions={
                <Button
                    disabled={!formik.values.hardwareType || formik.values.firmwareId === 1 || !formik.values.file}
                    onClick={handleSubmit}
                    color="primary"
                >
                    {t("action_upload")}
                </Button>
            }
        >
            <form onSubmit={handleSubmit}>
                <Stack spacing={2} pt={1}>
                    <input
                        type={"file"}
                        id="file"
                        name="name"
                        onChange={(e) => {
                            if (e.currentTarget.files?.length === 0 || !e.currentTarget.files) {
                                formik.setFieldValue("file", undefined);
                                return;
                            } else {
                                const file = e.currentTarget.files[0];
                                if (file) {
                                    formik.setFieldValue("file", file);
                                }
                            }
                        }}
                    />
                    <FormControl fullWidth error={formik.touched.hardwareType && Boolean(formik.errors.hardwareType)}>
                        <InputLabel id="hardwareType-label">{t("device_hardwareType")}</InputLabel>
                        <Select
                            fullWidth
                            labelId="hardwareType-label"
                            id="hardwareType"
                            name="hardwareType"
                            label={t("device_hardwareType")}
                            onChange={(e) => {
                                const val = e.target.value;
                                formik.setFieldValue("hardwareType", val);
                            }}
                        >
                            {Object.values(HardwareType).map((type, index) => {
                                return (
                                    <MenuItem value={type} key={`hardwareType_${index}`}>
                                        {type}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <FirmwareAutocomplete
                        label={t("firmware_version")}
                        additionalFilters={[
                            {
                                f: FirmwareField.HARDWARE_TYPE,
                                o: FilterOperator.EQ,
                                v: formik.values.hardwareType || "",
                            },
                            {
                                f: FirmwareField.STATE,
                                o:
                                    session?.permissionLevel !== PermissionLevel.ADMIN
                                        ? FilterOperator.EQ
                                        : FilterOperator.NOT,
                                v:
                                    session?.permissionLevel !== PermissionLevel.ADMIN
                                        ? FirmwareState.STABLE
                                        : FirmwareState.DEPRECATED,
                            },
                        ]}
                        value={formik.values.firmwareId}
                        onChange={(value) => {
                            if (value) {
                                formik.setFieldValue("firmwareId", value.id);
                            }
                        }}
                    />
                </Stack>
            </form>
        </DefaultDialog>
    );
}
